import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { NAV_BG, AMBER, AMBER_DARK } from '../theme';

// Users mostly open this app as a home-screen PWA, which the OS often just
// resumes from memory instead of reloading, so it can keep running a stale
// build until the process is killed (e.g. a device reboot). This checks a
// build id fetched fresh from the server whenever the app regains focus (and
// periodically while left open) and offers a manual refresh when it changes,
// rather than force-reloading a driver mid-route.
const CHECK_INTERVAL_MS = 60 * 60 * 1000;

export default function UpdateBanner() {
  const { t } = useTranslation();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const knownVersion = useRef(null);

  const checkVersion = useCallback(async () => {
    // The browser's own background check for a new service worker script is
    // throttled to roughly once every 24h. Poking it here — on the same
    // schedule as the version check below — lets a new worker (and the fix
    // it carries for stale PWA shells) land within the hour instead of
    // waiting on that throttle.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => reg && reg.update()).catch(() => {});
    }
    try {
      const res = await fetch('/version.json', { cache: 'no-store' });
      if (!res.ok) return;
      const { version } = await res.json();
      if (!version) return;
      if (knownVersion.current === null) {
        knownVersion.current = version;
      } else if (version !== knownVersion.current) {
        setUpdateAvailable(true);
      }
    } catch (_) {
      // offline or request failed — nothing to report
    }
  }, []);

  useEffect(() => {
    checkVersion();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [checkVersion]);

  if (!updateAvailable) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        background: `linear-gradient(90deg, ${NAV_BG} 0%, #163571 100%)`,
        borderBottom: `2px solid ${AMBER}`,
      }}
    >
      <RefreshIcon sx={{ color: AMBER, fontSize: 20, flexShrink: 0 }} />
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#e2e8f0', flex: 1 }}>
        {t('update_banner.message')}
      </Typography>
      <Button
        size="small"
        variant="contained"
        disableElevation
        onClick={() => window.location.reload()}
        sx={{
          background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
          color: NAV_BG,
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 700,
          textTransform: 'none',
          borderRadius: 1.5,
          fontSize: '0.8rem',
          px: 1.5,
          py: 0.4,
          flexShrink: 0,
        }}
      >
        {t('update_banner.refresh')}
      </Button>
    </Box>
  );
}
